import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { ThirdPartyStatus } from '@risksmart-app/domain/src/types/consts/third-party-status';
import type { ThirdPartyType } from '@risksmart-app/domain/src/types/consts/third-party-type';
import type { UpdateThirdPartyMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * tRPC input type derived from GraphQL UpdateThirdPartyInput.
 * Adds Id. Title/Type/Status are required by the tRPC router (Type/Status are
 * required native enums). `Criticality` is OPTIONAL — the column is a NULLABLE
 * integer with a CHECK 1..4, and the form offers a blank "-" option. Viewer
 * arrays are nullish for the tri-state (undefined = no change, [] = clear-all).
 */
export type UpdateThirdPartyTRPCInput = {
  Id: string;
  Title: string;
  Type: ThirdPartyType;
  Status: ThirdPartyStatus;
  Criticality?: number | null;
  Description?: string | null;
  CompanyName?: string | null;
  CompaniesHouseNumber?: string | null;
  Address?: string | null;
  CityTown?: string | null;
  Postcode?: string | null;
  Country?: string | null;
  PrimaryContactName?: string | null;
  ContactName?: string | null;
  ContactEmail?: string | null;
  CompanyDomain?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
  OwnerUserIds?: string[];
  OwnerGroupIds?: string[];
  ContributorUserIds?: string[];
  ContributorGroupIds?: string[];
  TagTypeIds?: string[];
  DepartmentTypeIds?: string[];
  ViewerUserIds?: string[] | null;
  ViewerGroupIds?: string[] | null;
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): UpdateThirdPartyMutation => {
  return {
    updateThirdPartyApi: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useUpdateThirdPartyTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.thirdParty.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.thirdParty.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.thirdParty.getById.queryKey(),
        });
      },
    }),
  });

  return {
    updateThirdParty: async (
      variables: UpdateThirdPartyTRPCInput
    ): Promise<UpdateThirdPartyMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
